import { CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { PayOSInputField } from "./seting-payment-input-field";
 
interface PaymentField {
  name: string;
  label: string;
  placeholder: string;
  isRequired?: boolean;
}

interface PaymentConnectionCardProps<T> {
  title: string;
  fields: PaymentField[];
  type: 'payos' | 'webhook' | 'viettinbank';
  getDetails: () => { fetch: () => void; response: any; loading: boolean };
  updateSettings: () => { fetch: (payload: T) => void; response: any; loading: boolean };
  disconnect: () => { fetch: () => void; response: any; loading: boolean };
}

export const PaymentConnectionCard = <T,>({ title, fields, getDetails, updateSettings, disconnect }: PaymentConnectionCardProps<T>) => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isStatusConnected, setIsStatusConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | undefined>();

  const { fetch: fetchDetails, response: detailsRes, loading: loadingDetails } = getDetails();
  const { fetch: fetchUpdate, response: updateRes, loading: loadingUpdate } = updateSettings();
  const { fetch: fetchDisconnect, response: disconnectRes, loading: loadingDisconnect } = disconnect();

  useEffect(() => {
    fetchDetails();
  }, []);

  useEffect(() => {
    if (detailsRes && isSuccess(detailsRes)) {
      const data = detailsRes.data;
      const isConnected = !!(data && data.client_id && data.api_key && data.checksum_key);
      setIsStatusConnected(isConnected);
      setLastUpdated(data?.updated_at ? data.updated_at : undefined);

      const newSettings = fields.reduce((acc, field) => {
        acc[field.name] = isConnected ? data[field.name] || "" : "";
        return acc;
      }, {} as Record<string, string>);
      setSettings(newSettings);
    }
  }, [detailsRes]);

  useEffect(() => {
    if (updateRes) {
      if (isSuccess(updateRes)) {
        showToast({ message: "Kết nối thành công!", type: "success" });
        fetchDetails();
      } else {
        showToast({ message: updateRes.message || "Có lỗi xảy ra", type: "error" });
      }
    }
  }, [updateRes]);

  useEffect(() => {
    if (disconnectRes) {
      if (isSuccess(disconnectRes)) {
        showToast({ message: "Đã hủy kết nối", type: "info" });
        setIsStatusConnected(false);
        setLastUpdated(undefined);
      } else {
        showToast({ message: disconnectRes.message || "Có lỗi xảy ra", type: "error" });
      }
    }
  }, [disconnectRes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const requiredFields = fields.filter((field) => field.isRequired !== false);
    const allRequiredFieldsFilled = requiredFields.every((field) => settings[field.name] && settings[field.name].trim() !== "");
    if (!allRequiredFieldsFilled) {
      showToast({ message: "Vui lòng điền đầy đủ thông tin", type: "error" });
      return;
    }
    fetchUpdate(settings as T);
  };

  const handleDisconnect = () => {
    fetchDisconnect();
  };

  const statusText = isStatusConnected ? "Đã kết nối" : "Chưa kết nối";
  const statusColor = isStatusConnected ? "text-green-600" : "text-red-500";
  const StatusIcon = isStatusConnected ? CheckCircle : XCircle;

  return (
    <div className="w-full max-w-4xl p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold tracking-wide text-[#17A34A] flex items-center">{title}</h3>
        <p className="text-sm mt-1 font-medium flex items-center space-x-1">
          <StatusIcon size={16} className={statusColor} />
          <span className="text-gray-600">Trạng thái:</span>
          <span className={`font-semibold ${statusColor}`}>{loadingDetails ? "Đang tải..." : statusText}</span>
          {lastUpdated && <span className="text-gray-500 ml-4">Cập nhật lần cuối: {lastUpdated}</span>}
        </p>
      </div>

      <div className="space-y-4">
        {fields.map((field) => (
          <PayOSInputField key={field.name} label={field.label} value={settings[field.name] ?? ""} placeholder={field.placeholder} isRequired={field.isRequired !== false} onChange={handleInputChange} name={field.name} />
        ))}
      </div>

      <div className="flex justify-end pt-6">
        {(() => {
          if (loadingUpdate || loadingDisconnect || loadingDetails) {
            return <div className="spinner">Đang xử lý...</div>;
          }
          if (isStatusConnected) {
            return (
              <button onClick={handleDisconnect} className="px-6 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150">HỦY KẾT NỐI</button>
            );
          }
          return (
            <button onClick={handleSubmit} className="px-6 py-2 text-sm font-medium text-white bg-green-500 border border-transparent rounded-md shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150">KẾT NỐI</button>
          );
        })()}
      </div>
    </div>
  );
};